import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const ViewSearches = ({ onEditRecord }) => {
  const { authenticatedFetch } = useAuth();
  const [searchData, setSearchData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 20,
    totalRecords: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  });
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const loadSearchCriteria = useCallback(async (page = 1, limit = rowsPerPage, searchQuery = '') => {
    setLoading(true);
    setError('');
    
    try {
      let url;
      if (searchQuery.trim()) {
        // Use search endpoint for server-side filtering
        url = `/api/search-criteria/search?q=${encodeURIComponent(searchQuery)}&page=${page}&limit=${limit}`;
      } else {
        // Use regular endpoint for pagination without search
        url = `/api/search-criteria?page=${page}&limit=${limit}`;
      }
      
      const response = await authenticatedFetch(url);
      const result = await response.json();
      
      if (result.success) {
        setSearchData(result.data);
        setPagination(result.pagination);
      } else {
        setError('Error loading data: ' + result.message);
      }
    } catch (error) {
      console.error('Error loading search criteria:', error);
      setError('Error loading search criteria. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [rowsPerPage, authenticatedFetch]);

  // Debounced search effect
  useEffect(() => {
    const delayTimer = setTimeout(() => {
      if (searchTerm !== undefined) {
        loadSearchCriteria(1, rowsPerPage, searchTerm);
      }
    }, 500);

    return () => clearTimeout(delayTimer);
  }, [searchTerm, rowsPerPage, loadSearchCriteria]);

  // Load data when component mounts
  useEffect(() => {
    loadSearchCriteria(1, rowsPerPage, '');
  }, [loadSearchCriteria, rowsPerPage]); // Reload when rowsPerPage changes

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      loadSearchCriteria(newPage, rowsPerPage, searchTerm);
    }
  };

  const handleRowsPerPageChange = (newLimit) => {
    setRowsPerPage(newLimit);
    loadSearchCriteria(1, newLimit, searchTerm); // Reset to first page with new limit
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getActiveFilters = (row) => {
    const filters = [];
    if (row.RNameON) filters.push('Name');
    if (row.RCityOn) filters.push('City');
    if (row.RZipOn) filters.push('ZIP');
    if (row.RCtryOn) filters.push('Country');
    if (row.RStreetOn) filters.push('Street');
    return filters.join(', ') || 'None';
  };

  const handleRowClick = (row) => {
    setSelectedRowId(row.ID);
    if (onEditRecord) {
      onEditRecord(row);
    }
  };

  return (
    <div className="tab-content active view-searches-container">
      <div className="view-controls">
        <div className="control-row">
          <button 
            onClick={() => loadSearchCriteria(pagination?.currentPage || 1, rowsPerPage, searchTerm)} 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh Data'}
          </button>
          
          <div className="search-box">
            <label htmlFor="searchTerm">Search:</label>
            <input
              type="text"
              id="searchTerm"
              placeholder="Search in all fields..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          
          <div className="rows-per-page">
            <label htmlFor="rowsPerPage">Rows per page:</label>
            <select 
              id="rowsPerPage"
              value={rowsPerPage} 
              onChange={(e) => handleRowsPerPageChange(parseInt(e.target.value))}
              disabled={loading}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
        
        <div className="search-info">
          {searchTerm && (
            <p className="search-results-info">
              Showing {searchData.length} results for "{searchTerm}"
            </p>
          )}
        </div>
        
        <div className="search-instructions">
          <p>💡 <strong>Tip:</strong> Click on any row or use the "Edit" button to edit search criteria in the Register tab.</p>
        </div>
      </div>
      
      <div className="search-results">
        {error && (
          <p className="error">{error}</p>
        )}
        
        {loading && (
          <p>Loading search criteria...</p>
        )}
        
        {!loading && !error && searchData.length === 0 && !searchTerm && (
          <p>No search criteria found. Click "Refresh Data" to load existing search criteria.</p>
        )}
        
        {!loading && !error && searchData.length === 0 && searchTerm && (
          <p className="no-results">No results found for "{searchTerm}". Try a different search term.</p>
        )}
        
        {!loading && !error && searchData.length > 0 && (
          <>
            <div className="pagination-info">
              <p>
                {searchTerm ? (
                  `Showing ${searchData.length} search results`
                ) : (
                  `Showing ${((pagination?.currentPage || 1) - 1) * (pagination?.limit || rowsPerPage) + 1} to ${Math.min((pagination?.currentPage || 1) * (pagination?.limit || rowsPerPage), pagination?.totalRecords || searchData.length)} of ${pagination?.totalRecords || searchData.length} entries`
                )}
              </p>
            </div>
            
            <div className="table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Active</th>
                    <th>Apply From</th>
                    <th>Apply To</th>
                    <th>Receiver Name</th>
                    <th>City</th>
                    <th>ZIP</th>
                    <th>Country</th>
                    <th>Street</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Reference</th>
                    <th>Last Check</th>
                    <th>Filters</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {searchData.map((row, index) => (
                    <tr 
                      key={index}
                      className={`data-row ${selectedRowId === row.ID ? 'row-selected' : ''}`}
                      onClick={() => handleRowClick(row)}
                    >
                      <td className={row.Active ? 'status-active' : 'status-inactive'}>
                        {row.Active ? '✓ Active' : '✗ Inactive'}
                      </td>
                      <td>{formatDate(row.ApplyFrom)}</td>
                      <td>{formatDate(row.ApplyTo)}</td>
                      <td>{row.ReceiverName || ''}</td>
                      <td>{row.ReceiverCity || ''}</td>
                      <td>{row.ReceiverZip || ''}</td>
                      <td>{row.ReceiverCtry || ''}</td>
                      <td>{row.ReceiverStreet || ''}</td>
                      <td>{row.Email || ''}</td>
                      <td>{row.Phone || ''}</td>
                      <td>{row.Reference || ''}</td>
                      <td>{formatDateTime(row.LastCheck)}</td>
                      <td>{getActiveFilters(row)}</td>
                      <td>
                        <button 
                          className="btn btn-secondary btn-small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowClick(row);
                          }}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="pagination-controls">
              <div className="pagination-info">
                <span>Page {pagination?.currentPage || 1} of {pagination?.totalPages || 1}</span>
              </div>
              
              <div className="pagination-buttons">
                <button 
                  onClick={() => handlePageChange(1)}
                  disabled={!pagination?.hasPrev || loading}
                  className="btn btn-secondary"
                >
                  First
                </button>
                <button 
                  onClick={() => handlePageChange((pagination?.currentPage || 1) - 1)}
                  disabled={!pagination?.hasPrev || loading}
                  className="btn btn-secondary"
                >
                  Previous
                </button>
                
                <span className="page-numbers">
                  {Array.from({ length: Math.min(5, pagination?.totalPages || 1) }, (_, i) => {
                    const currentPage = pagination?.currentPage || 1;
                    const totalPages = pagination?.totalPages || 1;
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => handlePageChange(pageNum)}
                        disabled={loading}
                        className={`btn ${pageNum === currentPage ? 'btn-primary' : 'btn-secondary'}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </span>
                
                <button 
                  onClick={() => handlePageChange((pagination?.currentPage || 1) + 1)}
                  disabled={!pagination?.hasNext || loading}
                  className="btn btn-secondary"
                >
                  Next
                </button>
                <button 
                  onClick={() => handlePageChange(pagination?.totalPages || 1)}
                  disabled={!pagination?.hasNext || loading}
                  className="btn btn-secondary"
                >
                  Last
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViewSearches;
